import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import type { ArAgingCustomerRow } from '@dcc-erp/shared';

import { AgingReport } from '../components/ar/AgingReport';
import { PageHead } from '../components/layout';
import { useActiveCompany, useActiveCompanyId, useArAging } from '../lib/queries';
import { exportRows, todayStamp, type ExportColumn } from '../lib/xlsx';

export const Route = createFileRoute('/ar/aging')({ component: ArAgingPage });

export function ArAgingPage() {
  const companyId = useActiveCompanyId();
  const company = useActiveCompany();
  const navigate = useNavigate();
  // Empty string = "as of today" (server default); a chosen date narrows it.
  const [asOf, setAsOf] = useState<string>('');
  const { data, isPending, error } = useArAging(companyId, asOf || undefined);

  // Export column specs — header keys mirror the wire shape so an exported file
  // is self-describing. Decimal strings coerced to numbers for numeric cells.
  const cols: ExportColumn<ArAgingCustomerRow>[] = [
    { header: 'customerCode', value: (r) => r.customerCode, width: 14 },
    { header: 'customerName', value: (r) => r.customerName, width: 28 },
    { header: 'notDue', value: (r) => Number(r.notDue), width: 14 },
    { header: 'd1_30', value: (r) => Number(r.d1_30), width: 12 },
    { header: 'd31_60', value: (r) => Number(r.d31_60), width: 12 },
    { header: 'd61_90', value: (r) => Number(r.d61_90), width: 12 },
    { header: 'over90', value: (r) => Number(r.over90), width: 12 },
    { header: 'total', value: (r) => Number(r.total), width: 14 },
  ];

  async function handleExport() {
    await exportRows(
      `อายุหนี้_${company?.code ?? 'COMPANY'}_${todayStamp()}.xlsx`,
      'อายุหนี้',
      cols,
      data?.customers ?? [],
    );
  }

  return (
    <div className="page">
      <PageHead crumbs={['ลูกหนี้การค้า', 'รายงานอายุหนี้']} title="รายงานอายุหนี้ (AR Aging)" />
      <div className="filter-row">
        <input
          className="input w-sm"
          type="date"
          value={asOf}
          onChange={(e) => setAsOf(e.target.value)}
        />
      </div>
      {isPending && (
        <div className="muted" style={{ padding: 24, textAlign: 'center' }}>
          กำลังโหลด…
        </div>
      )}
      {error && (
        <div className="neg" style={{ padding: 24, textAlign: 'center' }}>
          โหลดไม่สำเร็จ: {error.message}
        </div>
      )}
      {data && (
        <AgingReport
          data={data}
          onExport={() => void handleExport()}
          onCustomer={(id) => void navigate({ to: '/ar', search: { customerId: id } })}
        />
      )}
    </div>
  );
}
